import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  MoreVertical,
  Pencil,
  Plus,
} from "lucide-react";
import {
  fetchUsers,
  getBalagruha,
  getSchedules,
  updateBalagruhaSchedule,
  updateSchedule,
  updateScheduleStatus,
} from "../api";
import showToast from "../utils/toast";
import ResizableSplit from "../components/adminDailySchedule/ResizableSplit";
import "../components/adminDailySchedule/AdminDailySchedule.css";
import AdminLessonPlan from "./AdminLessonPlan";
import AdminLessonPlanSchedule from "./AdminLessonPlanSchedule";
import { RescheduleVisitModal, ShareVisitModal, visitShareText } from "./DailyScheduleVisitModals";
import { ClassTimingsModal, TotalVisitsModal } from "./DailyScheduleCentreModals";
import DateRangePicker, { DEFAULT_RANGE_DAYS, daysBetween } from "./DailyScheduleRangePicker";
import {
  CENTRE_COLORS,
  ClassTimings,
  Pin,
  addDays,
  centreIdOf,
  coachIdOf,
  confirmDialog,
  escapeHtml,
  resultDialog,
  dateKey,
  dayMonth,
  flattenSchedules,
  fromKey,
  hhmm,
  idOf,
  isCoach,
  isStudent,
  rangeLabel,
  scheduleDate,
  shortTime,
  startOfDay,
  useOutsideClick,
} from "./dailyScheduleShared";
import "./AdminDailySchedule.css";
import SearchableSelect from "../components/common/SearchableSelect";

const ACTIVE_PIN = "#1677ff";
// Default width of the centre table next to the calendar (% of the row); the divider between them is draggable.
const CENTRES_SPLIT_PERCENT = 30;
const CENTRES_SPLIT_KEY = "ds-centres-split";

// The coaches of one schedule (one Balgruha, one day, one start and end time) share a single card. Each
// coach is still a separate schedule in the API, so a "visit" here is a group of schedules. Another
// schedule at the same Balgruha that day at another time gets its own card, as on the View / Edit page
// (`scheduleSlot` in AdminLessonPlanSchedule).
const momentOf = (time) => new Date(time).getTime();
const groupVisits = (schedules) => {
  const groups = {};
  schedules.forEach((s) => {
    const key = `${dateKey(scheduleDate(s))}|${centreIdOf(s)}|${momentOf(s.startTime)}|${momentOf(s.endTime)}`;
    (groups[key] ||= { key, centreId: centreIdOf(s), date: dateKey(scheduleDate(s)), all: [] }).all.push(s);
  });
  return Object.values(groups).map((g) => {
    const live = g.all.filter((s) => s.status !== "cancelled");
    const members = (live.length ? live : g.all).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const latestEnd = members.reduce((end, s) => (new Date(s.endTime) > new Date(end) ? s.endTime : end), members[0].endTime);
    const status = !live.length ? "cancelled" : live.every((s) => s.status === "completed") ? "completed" : "active";
    return { ...g, members, status, startTime: members[0].startTime, endTime: latestEnd };
  });
};

// The calendar scrolls and clips its overflow, so the ⋮ menu is drawn over the page (fixed, next to the
// button) instead of inside the narrow day column, where it was cut off.
function useFloatingMenu(open, buttonRef, menuRef, close) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    // The button is as tall as the card, so anchor to its ⋮ icon to stay next to the dots.
    const button = (buttonRef.current?.querySelector("svg") || buttonRef.current)?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!button || !menu) return;
    const below = button.bottom + 4;
    const top = below + menu.height <= window.innerHeight - 8 ? below : Math.max(8, button.top - 4 - menu.height);
    const left = Math.max(8, Math.min(button.right - menu.width, window.innerWidth - menu.width - 8));
    setPos({ top, left });
  }, [open, buttonRef, menuRef]);
  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => { if (!buttonRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") { close(); buttonRef.current?.focus(); } };
    const onMove = (e) => { if (!menuRef.current?.contains(e.target)) close(); };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", close);
    };
  }, [open, buttonRef, menuRef, close]);
  return pos;
}

function VisitCard({ visit, centreName, coachNames, onEdit, onStatus, onView, onShare, onReschedule }) {
  const [menu, setMenu] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const close = useCallback(() => setMenu(false), []);
  const pos = useFloatingMenu(menu, buttonRef, menuRef, close);
  const { status } = visit;
  const pick = (action) => () => { setMenu(false); action(visit); };

  return (
    <div className={`ds-visit ds-visit--${status}`}>
      <button type="button" className="ds-visit__body" onClick={() => onEdit(visit)}>
        <span className="ds-visit__text">
          <strong>
            {status === "completed" ? (
              <span className="ds-visit__done"><Check size={10} strokeWidth={4} /></span>
            ) : (
              <Pin color={status === "cancelled" ? "#ec1f6f" : ACTIVE_PIN} />
            )}
            <span className="ds-visit__name">{centreName}</span>
          </strong>
          <span className="ds-visit__coaches">{coachNames}</span>
          <span>{shortTime(visit.startTime)} – {shortTime(visit.endTime)}</span>
          {status === "cancelled" && <b>Cancelled</b>}
        </span>
      </button>
      <button type="button" className="ds-visit__menu-btn" aria-label="Visit actions" aria-haspopup="menu" aria-expanded={menu} ref={buttonRef} onClick={() => setMenu((m) => !m)}>
        <MoreVertical size={16} />
      </button>
      {menu && createPortal(
        <div className="ds-menu ds-menu--floating" role="menu" aria-label={`${centreName} visit actions`} ref={menuRef} style={pos ? { top: pos.top, left: pos.left } : { visibility: "hidden" }}>
          <button type="button" role="menuitem" onClick={pick(onView)}>View visit</button>
          <button type="button" role="menuitem" onClick={() => { setMenu(false); onEdit(visit); }}>Edit visit</button>
          {/* Only cancelled visits can be rescheduled; it schedules them again (see `reschedule` in the page). */}
          {status === "cancelled" && <button type="button" role="menuitem" onClick={pick(onReschedule)}>Reschedule visit</button>}
          <button type="button" role="menuitem" onClick={pick(onShare)}>Share visit</button>
          {status === "active" && <button type="button" role="menuitem" onClick={() => { setMenu(false); onStatus(visit, "completed"); }}>Mark completed</button>}
          {status !== "cancelled" && <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(false); onStatus(visit, "cancelled"); }}>Cancel visit</button>}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function AdminDailySchedule() {
  const navigate = useNavigate();
  const location = useLocation();
  // /daily-schedule/create, /view and /edit show the lesson plan page instead of the calendar.
  const planMode = /\/daily-schedule\/(create|view|edit)$/.exec(location.pathname)?.[1] || "";
  const [balagruhas, setBalagruhas] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [coach, setCoach] = useState("");
  const [balagruha, setBalagruha] = useState("");
  const [start, setStart] = useState(() => startOfDay(new Date()));
  // Days shown from `start`: two weeks by default, or the range picked in the date range popover.
  const [span, setSpan] = useState(DEFAULT_RANGE_DAYS);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef(null);
  const closeRange = useCallback(() => setRangeOpen(false), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  // Centre table cell being edited: { id, field: "timings" | "visits" }.
  const [editingCentre, setEditingCentre] = useState(null);
  const [createMenu, setCreateMenu] = useState(false);
  const createRef = useRef(null);
  const closeCreateMenu = useCallback(() => setCreateMenu(false), []);
  useOutsideClick(createRef, closeCreateMenu, createMenu);

  const end = useMemo(() => addDays(start, span - 1), [start, span]);
  // Rows of seven days from `start`; days after `end` in the last row are shown greyed out.
  const weeks = useMemo(() => Array.from({ length: Math.ceil(span / 7) }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i))), [start, span]);
  const stepLabel = span === DEFAULT_RANGE_DAYS ? "two weeks" : `${span} days`;
  const applyRange = (from, to) => {
    setStart(startOfDay(from));
    setSpan(daysBetween(from, to) + 1);
    setRangeOpen(false);
  };
  const coaches = useMemo(() => users.filter(isCoach), [users]);
  const colorOf = useMemo(() => {
    const map = {};
    balagruhas.forEach((b, i) => { map[b._id] = CENTRE_COLORS[i % CENTRE_COLORS.length]; });
    return map;
  }, [balagruhas]);
  const centreName = useMemo(() => Object.fromEntries(balagruhas.map((b) => [b._id, b.name])), [balagruhas]);
  const centreTimings = useMemo(() => Object.fromEntries(balagruhas.map((b) => [b._id, b.classTimings || []])), [balagruhas]);
  const userName = useMemo(() => Object.fromEntries(users.map((u) => [u._id || u.id, u.name])), [users]);
  const centreOptions = useMemo(() => balagruhas.map((b) => ({ id: b._id, name: b.name })), [balagruhas]);
  const coachOptions = useMemo(() => coaches.map((c) => ({ id: c._id || c.id, name: c.name })), [coaches]);

  const load = useCallback(async () => {
    const ids = (balagruha ? [balagruha] : balagruhas.map((b) => b._id)).filter(Boolean);
    if (!ids.length) return;
    try {
      const result = await getSchedules({ balagruhaIds: ids, assignedTo: coach || undefined, startDate: dateKey(start), endDate: dateKey(end), status: [] });
      setEvents(flattenSchedules(result));
    } catch {
      showToast("Unable to load the schedule", "error");
    }
  }, [balagruha, balagruhas, coach, start, end]);

  useEffect(() => {
    Promise.all([getBalagruha(), fetchUsers()])
      .then(([b, u]) => { const list = b?.data?.balagruhas || b?.data; setBalagruhas(Array.isArray(list) ? list : []); setUsers(u?.data || []); })
      .catch(() => showToast("Unable to load schedule filters", "error"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { if (!loading && balagruhas.length && !planMode) load(); }, [loading, balagruhas, load, planMode]);

  const visits = useMemo(() => groupVisits(events), [events]);
  const visitsByDay = useMemo(() => {
    const map = {};
    visits.forEach((v) => { (map[v.date] ||= []).push(v); });
    Object.values(map).forEach((list) => list.sort((a, b) => new Date(a.startTime) - new Date(b.startTime)));
    return map;
  }, [visits]);

  const centreRows = useMemo(() => {
    const weekOneEnd = dateKey(weeks[0][6]);
    return balagruhas
      .filter((b) => !balagruha || b._id === balagruha)
      .map((b) => {
        const centreVisits = visits.filter((v) => v.centreId === b._id && v.status !== "cancelled");
        return {
          ...b,
          children: users.filter((u) => isStudent(u) && (u.balagruhaIds || []).some((id) => idOf(id) === b._id)).length,
          thisWeek: centreVisits.filter((v) => v.date <= weekOneEnd).length,
          counted: centreVisits.length,
          // Total Visits typed in on this page wins; without one, the visits in the calendar are counted.
          total: Number.isFinite(b.totalVisits) ? b.totalVisits : centreVisits.length,
          computers: (b.assignedMachines || []).length,
        };
      });
  }, [balagruhas, balagruha, visits, users, weeks]);

  const openCreate = (day) => {
    setCreateMenu(false);
    const params = new URLSearchParams();
    if (day) params.set("date", dateKey(day));
    if (balagruha) params.set("balagruha", balagruha);
    if (coach) params.set("coach", coach);
    const qs = params.toString();
    navigate(`/daily-schedule/create${qs ? `?${qs}` : ""}`);
  };
  // View / Edit open the lesson plan page for the card's schedule.
  // (AdminLessonPlanSchedule finds that schedule and lists any others at the Balgruha that day.)
  const openSchedule = (visit, mode) => {
    const params = new URLSearchParams({ id: visit.members[0]._id, date: visit.date, balagruha: visit.centreId });
    navigate(`/daily-schedule/${mode}?${params}`);
  };

  const changeStatus = async (visit, status) => {
    const cancelling = status === "cancelled";
    const summary = `<b>${escapeHtml(nameOfCentre(visit.members[0]))}</b><br/>${escapeHtml(dayMonth(fromKey(visit.date)))} ${fromKey(visit.date).getFullYear()}, ${escapeHtml(shortTime(visit.startTime))} – ${escapeHtml(shortTime(visit.endTime))}<br/>Coach: ${escapeHtml(coachNamesOf(visit))}`;
    const confirmed = await confirmDialog({
      title: cancelling ? "Cancel this visit?" : "Mark this visit as completed?",
      html: `${summary}${cancelling ? "<br/><br/>The visit will be cancelled for all its coaches." : ""}`,
      confirmText: cancelling ? "Yes, cancel visit" : "Yes, mark completed",
      cancelText: cancelling ? "Keep visit" : "Not yet",
      danger: cancelling,
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      for (const member of visit.members.filter((m) => m.status !== status)) {
        const result = await updateScheduleStatus(member._id, status);
        if (result && result.success === false) throw new Error(result.message);
      }
      resultDialog("success", cancelling ? "Visit cancelled" : "Visit marked completed");
    } catch (err) {
      resultDialog("error", cancelling ? "Could not cancel the visit" : "Could not update the visit", err?.response?.data?.message || err?.message || "Please try again.");
    } finally {
      setSaving(false);
      load();
    }
  };

  // Moves every coach of the visit to the new date and time. If one move fails (for example the coach
  // already has a visit then), the coaches already moved are put back so the visit isn't split.
  // A cancelled visit is scheduled again for all its coaches. Left at its date and time, each coach keeps
  // their own time; with a new date or time it is moved like an active visit.
  const reschedule = async ({ date, startTime: from, endTime: to }) => {
    const visit = rescheduling;
    const restoring = visit.status === "cancelled";
    const startTime = new Date(`${date}T${from}:00`);
    const endTime = new Date(`${date}T${to}:00`);
    if (!date || Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) return showToast("Enter a valid date and time", "error");
    if (startTime >= endTime) return showToast("Start time must be before end time", "error");
    const sameSlot = date === visit.date && from === hhmm(visit.startTime) && to === hhmm(visit.endTime);
    if (sameSlot && !restoring) return showToast("Pick a new date or time for the visit", "error");
    const changes = {
      ...(!sameSlot && { date, dateString: date, startTime: startTime.toISOString(), endTime: endTime.toISOString() }),
      ...(restoring && { status: "pending" }),
    };

    setSaving(true);
    const moved = [];
    try {
      for (const member of visit.members) {
        const result = await updateSchedule(changes, member._id)
          .catch((e) => ({ success: false, message: e?.response?.data?.message || e?.message }));
        if (result && result.success === false) {
          throw new Error(/overlapping/i.test(result.message || "")
            ? `${nameOfCoach(member)} already has a visit at this time`
            : `${nameOfCoach(member)}: ${result.message || "could not be moved"}`);
        }
        moved.push(member);
      }
      setRescheduling(null);
      showToast("Visit rescheduled", "success");
    } catch (err) {
      for (const member of moved) {
        try {
          await updateSchedule({
            ...(!sameSlot && { date: member.date, dateString: dateKey(scheduleDate(member)), startTime: member.startTime, endTime: member.endTime }),
            ...(restoring && { status: "cancelled" }),
          }, member._id);
        } catch { /* the reload below shows what was saved */ }
      }
      showToast(err?.message || "Could not reschedule the visit", "error");
    } finally {
      setSaving(false);
      load();
    }
  };

  // Saves a centre table edit to the Balagruha; resolves to an error message for the dialog, or nothing.
  const saveCentre = async (changes) => {
    const id = editingCentre?.id;
    try {
      const result = await updateBalagruhaSchedule(id, changes);
      if (result && result.success === false) return result.message || "Could not save. Please try again.";
      // The API answers with both saved fields; fall back to what was sent.
      const saved = { ...changes, ...result?.data?.balagruha };
      setBalagruhas((list) => list.map((b) => (b._id === id ? {
        ...b,
        ...("classTimings" in saved && { classTimings: saved.classTimings }),
        ...("totalVisits" in saved && { totalVisits: saved.totalVisits }),
      } : b)));
      setEditingCentre(null);
      showToast(changes.classTimings ? "Available time saved" : "Total visits saved", "success");
      return "";
    } catch (err) {
      return err?.response?.data?.message || err?.message || "Could not save. Please try again.";
    }
  };

  const nameOfCentre = (s) => s.balagruha?.name || centreName[centreIdOf(s)] || "Balgruha";
  const nameOfCoach = (s) => s.assignedToUser?.name || userName[coachIdOf(s)] || "Unassigned";
  const coachNamesOf = (visit) => [...new Set(visit.members.map(nameOfCoach))].join(", ");

  const exportCsv = () => {
    const quote = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [...visits]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .map((v) => [v.date, fromKey(v.date).toLocaleDateString("en-GB", { weekday: "short" }), nameOfCentre(v.members[0]), coachNamesOf(v), hhmm(v.startTime), hhmm(v.endTime), v.status === "active" ? "scheduled" : v.status]);
    const csv = [["Date", "Day", "Centre", "Coaches", "Start", "End", "Status"], ...rows].map((r) => r.map(quote).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `daily-schedule-${dateKey(start)}-to-${dateKey(end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (planMode) {
    const planProps = { balagruhas, users, centreOptions, coachOptions, onDone: () => navigate("/daily-schedule") };
    return (
      <main className="ds-page ds-page--plan">
        {loading ? <div className="ds-board__empty">Loading…</div> : planMode === "create" ? (
          <AdminLessonPlan {...planProps} />
        ) : (
          <AdminLessonPlanSchedule mode={planMode} {...planProps} />
        )}
      </main>
    );
  }

  return (
    <main className="ds-page">
      <header className="ds-header">
        <div className="ds-header__title">
          <span className="ds-header__icon"><CalendarDays size={28} strokeWidth={1.8} /></span>
          <div>
            <h1>Daily Schedule</h1>
            <p>Plan, view and manage your visits across Balagruhas and Varkari Sansthas</p>
          </div>
        </div>

        <div className="ds-header__filters">
          <label>
            Balagruha
            <span className="ds-select">
              <SearchableSelect searchPlaceholder="Search balagruha..." value={balagruha} onChange={(e) => setBalagruha(e.target.value)}>
                <option value="">All Balagruhas</option>
                {balagruhas.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
              </SearchableSelect>
              <ChevronDown size={16} />
            </span>
          </label>
          <label>
            Coach
            <span className="ds-select">
              <SearchableSelect searchPlaceholder="Search coach..." value={coach} onChange={(e) => setCoach(e.target.value)}>
                <option value="">All Coaches</option>
                {coachOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SearchableSelect>
              <ChevronDown size={16} />
            </span>
          </label>
        </div>

        <div className="ds-range-wrap" ref={rangeRef}>
          <div className="ds-range">
            <button type="button" aria-label={`Previous ${stepLabel}`} onClick={() => setStart((d) => addDays(d, -span))}><ChevronLeft size={22} /></button>
            <button type="button" className="ds-range__label" aria-label={`Date range ${rangeLabel(start, end)}. Choose dates`} aria-haspopup="dialog" aria-expanded={rangeOpen} title="Choose dates" onClick={() => setRangeOpen((o) => !o)}>
              <CalendarDays size={20} />{rangeLabel(start, end)}<ChevronDown size={16} className="ds-range__chev" />
            </button>
            <button type="button" aria-label={`Next ${stepLabel}`} onClick={() => setStart((d) => addDays(d, span))}><ChevronRight size={22} /></button>
          </div>
          {rangeOpen && <DateRangePicker start={start} end={end} anchorRef={rangeRef} onApply={applyRange} onClose={closeRange} />}
        </div>

        <div className="ds-create" ref={createRef}>
          <button type="button" className="ds-create__main" onClick={() => openCreate(null)}><Plus size={22} />Add Schedule</button>
          <button type="button" className="ds-create__caret" aria-label="More options" onClick={() => setCreateMenu((m) => !m)}><ChevronDown size={20} /></button>
          {createMenu && (
            <div className="ds-menu ds-menu--right">
              <button type="button" onClick={() => { setCreateMenu(false); setStart(startOfDay(new Date())); setSpan(DEFAULT_RANGE_DAYS); }}>Jump to today</button>
            </div>
          )}
        </div>
      </header>

      <ResizableSplit
        leftLabel="centre table"
        rightLabel="calendar"
        defaultLeftPercent={CENTRES_SPLIT_PERCENT}
        storageKey={CENTRES_SPLIT_KEY}
        left={(
          <aside className="ds-centres">
            <table>
              <thead>
                <tr>
                  <th>Centre Name</th>
                  <th>No. of Children</th>
                  <th title="Visits in Week 1 / Total Visits (the number set here, else the visits in all the dates shown). Click a cell to edit">Visits This Week / Total Visits</th>
                  <th>No. of Computers</th>
                  <th title="Class timings set on the Balagruha form. Click a cell to edit">Available Time &amp; Date</th>
                </tr>
              </thead>
              <tbody>
                {centreRows.map((c) => (
                  <tr key={c._id}>
                    <td><span className="ds-centres__name"><Pin color={colorOf[c._id]} />{c.name}</span></td>
                    <td>{c.children}</td>
                    <td className="ds-centres__editable">
                      <button type="button" className="ds-centres__edit" aria-label={`Edit total visits of ${c.name}`} title="Edit total visits" onClick={() => setEditingCentre({ id: c._id, field: "visits" })}>
                        {c.thisWeek}/{c.total}
                        <Pencil size={10} className="ds-centres__pencil" aria-hidden="true" />
                      </button>
                    </td>
                    <td>{c.computers}</td>
                    <td className="ds-centres__timings ds-centres__editable">
                      <button type="button" className="ds-centres__edit" aria-label={`Edit available time of ${c.name}`} title="Edit available time" onClick={() => setEditingCentre({ id: c._id, field: "timings" })}>
                        <ClassTimings timings={c.classTimings} />
                        <Pencil size={10} className="ds-centres__pencil" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && !centreRows.length && <tr><td colSpan={5} className="ds-centres__empty">No centres found</td></tr>}
              </tbody>
            </table>
          </aside>
        )}
        right={(
          <section className="ds-board">
            {loading ? <div className="ds-board__empty">Loading schedule…</div> : weeks.map((days, w) => {
              // Four slots per day (up to four visits); grows only if a day already has more.
              const rows = Math.max(4, ...days.map((d) => (visitsByDay[dateKey(d)] || []).length));
              const weekEnd = days[6] > end ? end : days[6];
              return (
                <div className="ds-week" key={dateKey(days[0])}>
                  <div className="ds-week__bar">
                    <button type="button" aria-label="Previous week" onClick={() => setStart((d) => addDays(d, -7))}><ChevronLeft size={20} /></button>
                    <h2>Week {w + 1}</h2>
                    <span>{dayMonth(days[0])} – {dayMonth(weekEnd)} {weekEnd.getFullYear()}</span>
                    <button type="button" aria-label="Next week" className="ds-week__next" onClick={() => setStart((d) => addDays(d, 7))}><ChevronRight size={20} /></button>
                  </div>
                  <div className="ds-week__grid">
                    {days.map((day) => {
                      if (day > end) {
                        return (
                          <div className="ds-day ds-day--outside" key={dateKey(day)} aria-hidden="true">
                            <div className="ds-day__head">
                              <strong>{day.toLocaleDateString("en-GB", { weekday: "short" })}</strong>
                              <span>{dayMonth(day)}</span>
                            </div>
                          </div>
                        );
                      }
                      const dayVisits = visitsByDay[dateKey(day)] || [];
                      return (
                        <div className="ds-day" key={dateKey(day)}>
                          <div className="ds-day__head">
                            <strong>{day.toLocaleDateString("en-GB", { weekday: "short" })}</strong>
                            <span>{dayMonth(day)}</span>
                          </div>
                          <div className="ds-day__slots">
                            {dayVisits.map((v) => (
                              <VisitCard key={v.key} visit={v} centreName={nameOfCentre(v.members[0])} coachNames={coachNamesOf(v)} onEdit={(v) => openSchedule(v, "edit")} onStatus={changeStatus} onView={(v) => openSchedule(v, "view")} onShare={setSharing} onReschedule={setRescheduling} />
                            ))}
                            {Array.from({ length: rows - dayVisits.length }, (_, i) => (
                              <button type="button" className="ds-add" key={i} onClick={() => openCreate(day)}><Plus size={17} />Add Schedule</button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <footer className="ds-footer">
              <div className="ds-legend">
                <span><i className="is-completed" />Completed</span>
                <span><i className="is-active" />Scheduled (Active)</span>
                <span><i className="is-cancelled" />Cancelled</span>
              </div>
              <button type="button" className="ds-export" onClick={exportCsv}><Download size={20} />Export</button>
            </footer>
          </section>
        )}
      />

      {sharing && (
        <ShareVisitModal
          visit={sharing}
          centreName={nameOfCentre(sharing.members[0])}
          coachNames={coachNamesOf(sharing)}
          fallbackText={visitShareText({ visit: sharing, centreName: nameOfCentre(sharing.members[0]), coachNames: coachNamesOf(sharing) })}
          onClose={() => setSharing(null)}
        />
      )}
      {rescheduling && (
        <RescheduleVisitModal
          visit={rescheduling}
          centreName={nameOfCentre(rescheduling.members[0])}
          coachNames={coachNamesOf(rescheduling)}
          timings={centreTimings[rescheduling.centreId]}
          saving={saving}
          onClose={() => setRescheduling(null)}
          onSave={reschedule}
        />
      )}
      {editingCentre && (() => {
        const centre = centreRows.find((c) => c._id === editingCentre.id);
        if (!centre) return null;
        const close = () => setEditingCentre(null);
        return editingCentre.field === "timings"
          ? <ClassTimingsModal centre={centre} onClose={close} onSave={saveCentre} />
          : <TotalVisitsModal centre={centre} thisWeek={centre.thisWeek} counted={centre.counted} onClose={close} onSave={saveCentre} />;
      })()}
    </main>
  );
}
